// index.tsx
import React, { useState, useEffect, MouseEvent } from "react";
import { withRouter, RouteComponentProps } from "react-router-dom";
import {
  Typography,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  withStyles,
  WithStyles,
} from "@material-ui/core";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import CustomGrid from "../../../../../components/src/CustomGrid.web";
import withAlertBox from "../../../../../components/src/withAlertBox.Web";
import withToast from "../../../../../components/src/withSnackBar.Web";
import withLoader from "../../../../../components/src/withLoader.Web";
import withDialog from "../../../../../components/src/withDialog.web";
import withPermissions from "../../../../../components/src/withPermissions.web";
import StorageProvider from "../../../../../framework/src/StorageProvider.web";
import Editor from "../Editor";
import MessageEnum, { getName } from "../../../../../framework/src/Messages/MessageEnum";
import { Message } from "../../../../../framework/src/Message";
import { runEngine } from "../../../../../framework/src/RunEngine";
import styles from "./style";
import { Bookmark } from "@material-ui/icons";
import { api } from '../../../axios';
import { useMutation } from "@tanstack/react-query";


interface CFSSettings {
  survey_link: string;
  [key: string]: string | number | boolean | null | undefined;
}

interface EmailTemplatesProps
  extends RouteComponentProps,
    WithStyles<typeof styles> {
  showLoader: () => void;
  hideLoader: () => void;
  showToast: (args: {
    type: "success" | "error" | "info";
    message: string;
  }) => void;
  showAlert?: (args: { title?: string; message?: string }) => void;
}

function EmailTemplates(props: EmailTemplatesProps) {
  const { classes } = props;

  const [token, setToken] = useState<string>("");
  const [accountId, setAccountId] = useState<string>("");
  const [emailTemplates, setEmailTemplates] = useState<Record<string, string>>({});
  const [emailTemplateTitles, setEmailTemplateTitles] = useState<Record<string, string>>({});
  const [expandedEmailTemplate, setExpandedEmailTemplate] = useState<string | null>(null);
  const [editorLoaded, setEditorLoaded] = useState<boolean>(false);
  const [isCopied, setIsCopied] = useState<string | null>(null);
  const [isCopiedTemplateId, setIsCopiedTemplateId] = useState<string>("");
  const [cfsinitialValues, setCfsinitialValues] = useState<CFSSettings>({ survey_link: "" });

  const handleAccordionChange = (panel: string) => {
    setExpandedEmailTemplate((prev) => (prev === panel ? null : panel));
  };

  const copyTemplateContent = (html: string, template: string, event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setIsCopied(html);
    setIsCopiedTemplateId(`template_${template}`);
  };
  const saveEmailTemplateMutation = useMutation(
    async ({ token, templates }: { token: string; templates: any }) => {
      return await saveEmailTemplateApi(token, templates);
    }
  );
  const saveEmailTemplateApi = async (
    token: string,
    templates: Record<string, string>
  ) => {
    const response = await api.post(
      "survey_setting/email_template_update",
      {
        email_data: templates
      },
      {
        headers: {
          "content-type": "application/json",
          token: token
        }
      }
    );

    return response.data;
  };
  const saveEmailTemplate = async (templateKey: string, html: string) => {
    const oldHtml = emailTemplateTitles[templateKey];
    const newHtml = html;
      if (oldHtml.trim() === newHtml.trim()) {
      return;
    }
    props.showLoader();

    const updatedTemplates: Record<string, string> = {};

    Object.keys(emailTemplates).forEach((key) => {
      const raw = emailTemplateTitles[key];
      const readable = key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

      const existingTitle =
        raw?.match(/<title>(.*?)<\/title>/)?.[1] || readable;

      if (key === templateKey) {
        updatedTemplates[key] = `
          <html>
            <head>
              <title>${existingTitle}</title>
            </head>
            <body>
              ${html}
            </body>
          </html>
        `;
      } else {
        updatedTemplates[key] = raw;
      }
    });

    saveEmailTemplateMutation.mutate(
      { token, templates: updatedTemplates },
      {
        onSuccess: () => {
          props.hideLoader();
          props.showToast({
            type: "success",
            message: "Template saved successfully",
          });
        },
        onError: () => {
          props.hideLoader();
          props.showToast({
            type: "error",
            message: "Failed to save template",
          });
        }
      }
    );
  };


  useEffect(() => {
    if (isCopied && isCopiedTemplateId) {
      const element = document.getElementById(isCopiedTemplateId);
      if (element) {
        window.navigator.clipboard
          .writeText(element.innerText)
          .then(() => console.log("Copied:", element.innerText))
          .catch((err) => console.error("Clipboard copy failed:", err));
      } else {
        console.error("Element not found for copy:", isCopiedTemplateId);
      }

      const timer = setTimeout(() => {
        setIsCopied(null);
        setIsCopiedTemplateId("");
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [isCopied, isCopiedTemplateId]);

  const copySurveyLink = (values: CFSSettings) => {
    if (values.survey_link) {
      window.navigator.clipboard.writeText(values.survey_link);
    }
  };

  const getEmailTemplate = async (token: string) => {
    props.showLoader();
    try {
      const headers = {
        "content-type": "application/json",
        token,
      };

      const requestMessage = new Message(getName(MessageEnum.RestAPIRequestMessage));
      const getEmailTemplateCallId = requestMessage.messageId;

      requestMessage.addData(
        getName(MessageEnum.RestAPIResponceEndPointMessage),
        `survey_setting/insert_template_data`
      );
      requestMessage.addData(
        getName(MessageEnum.RestAPIRequestHeaderMessage),
        JSON.stringify(headers)
      );
      requestMessage.addData(getName(MessageEnum.RestAPIRequestMethodMessage), "GET");

      runEngine.sendMessage(requestMessage.id, requestMessage);

      runEngine.attachBuildingBlock(
        {
          receive: (from, message) => {
            const apiRequestCallId = message.getData(getName(MessageEnum.RestAPIResponceDataMessage));
            const responseJson = message.getData(getName(MessageEnum.RestAPIResponceSuccessMessage));

            if (apiRequestCallId === getEmailTemplateCallId) {
              props.hideLoader();

              if (responseJson?.data?.length > 0) {
                const templates = { ...responseJson.data[0].attributes };
                delete templates.id;
                setEmailTemplates({ ...templates});
                setEmailTemplateTitles({ ...templates });
              } else {
                props.showToast({ type: "error", message: "No email templates found" });
              }
            }
          },
          send: function () {},
        },
        [getName(MessageEnum.RestAPIResponceMessage)]
      );
    } catch (error) {
      props.hideLoader();
      props.showToast({ type: "error", message: "Failed to fetch email templates" });
    }
  };

  const getCFSSetting = async (token: string) => {
    props.showLoader();
    try {
      const headers = {
        "content-type": "application/json",
        token,
      };

      const requestMessage = new Message(getName(MessageEnum.RestAPIRequestMessage));
      const getCFSSettingCallId = requestMessage.messageId;

      requestMessage.addData(
        getName(MessageEnum.RestAPIResponceEndPointMessage),
        `bx_block_survey/survey_setting`
      );
      requestMessage.addData(
        getName(MessageEnum.RestAPIRequestHeaderMessage),
        JSON.stringify(headers)
      );
      requestMessage.addData(getName(MessageEnum.RestAPIRequestMethodMessage), "GET");

      runEngine.sendMessage(requestMessage.id, requestMessage);

      runEngine.attachBuildingBlock(
        {
          receive: (from, message) => {
            const apiRequestCallId = message.getData(getName(MessageEnum.RestAPIResponceDataMessage));
            const responseJson = message.getData(getName(MessageEnum.RestAPIResponceSuccessMessage));

            if (apiRequestCallId === getCFSSettingCallId) {
              props.hideLoader();

              if (responseJson?.data && responseJson?.data?.id) {
                const setting = responseJson?.data?.attributes;
                const paymentAccessToken = setting.cpa_charge_access_token;

                let survey_link =
                  window.location.origin +
                  `/surveyLandingPage?account_id=${accountId}&type=generalCFS`;

                if (paymentAccessToken) {
                  survey_link += `&access_token=${paymentAccessToken}`;
                }

                setCfsinitialValues({
                  ...setting,
                  survey_link,
                });
              } else {
               
              }
            }
          },
          send: function () {},
        },
        [getName(MessageEnum.RestAPIResponceMessage)]
      );
    } catch (error) {
      props.hideLoader();
      props.showToast({ type: "error", message: "Failed to load settings" });
    }
  };

  useEffect(() => {
    const init = async () => {
      const token = await StorageProvider.get("authToken");
      const accountId = await StorageProvider.get("accountId");
      const paymentAccessToken = await StorageProvider.get("paymentAccessToken");

      setToken(token);
      setAccountId(accountId);
      setEditorLoaded(true);

      let survey_link =
        window.location.origin +
        `/surveyLandingPage?account_id=${accountId}&type=generalCFS`;

      if (paymentAccessToken && paymentAccessToken !== "null") {
        survey_link += `&access_token=${paymentAccessToken || ""}`;
      }

      setCfsinitialValues((prev) => ({
        ...prev,
        survey_link,
      }));

      getCFSSetting(token);
      getEmailTemplate(token);
    };

    init();
  }, []);

  return (
    <Grid container justifyContent="center">
      <CustomGrid item container justifyContent="space-between" xs={12}>
        <Typography className={classes.pageHeading} color="primary">
          Email Templates
        </Typography>
      </CustomGrid>

      <CustomGrid item xs={12} className={classes.templateContainer}>
        <Grid container>
          <Grid item xs={12}>
            {Object.keys(emailTemplates).map((template) => {
    const ww = { ...emailTemplateTitles };
    const readableTitle = template
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
    const title =
      ww[template]?.includes("<title>")
        ? ww[template].match(/<title>(.*?)<\/title>/)?.[1] || readableTitle
        : readableTitle;
                return (
                  <Accordion
                    key={template}
                    square
                    expanded={expandedEmailTemplate === template}
                    onChange={() => handleAccordionChange(template)}
                  >
                    <AccordionSummary>
                      <Grid container alignItems="center" className={classes.accordionTitle}>
                        <Grid item xs={11}>
                          <div dangerouslySetInnerHTML={{ __html: title }} />
                        </Grid>
                        <Grid item xs={1}>
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();  
                              saveEmailTemplate(template, emailTemplates[template]);
                            }}
                          >
                            <Bookmark color="primary" />
                          </IconButton>

                          <IconButton
                            onClick={(event) =>
                              copyTemplateContent(emailTemplates[template], template, event)
                            }
                          >
                            <FileCopyIcon color="primary" />
                          </IconButton>
                        </Grid>
                      </Grid>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Editor
            name="description"
            onChange={(data: string) =>
              setEmailTemplates((prev) => ({
                ...prev,
                [template]: data,
              }))
            }
            value={emailTemplates[template]}
            editorLoaded={editorLoaded}
          />
                    </AccordionDetails>
                  </Accordion>
                );
              }
            )}
          </Grid>
        </Grid>
      </CustomGrid>

      {isCopied && (
        <Grid item xs={12}>
          <div id={isCopiedTemplateId} dangerouslySetInnerHTML={{ __html: isCopied }} />
        </Grid>
      )}
    </Grid>
  );
}

export default withStyles(styles)(
  withPermissions(
    withDialog(withToast(withLoader(withAlertBox(withRouter(EmailTemplates)))))
  )
);
